import React, { useState } from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiEdit,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiMapPin,
  FiPhone,
  FiMail,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiDownload,
  FiRefreshCw,
  FiPlus,
  FiX,
  FiSend,
  FiUser,
} from "react-icons/fi";
import { usersAPI } from "../../services/api";

const EmployeeManagementContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
  overflow-x: hidden;
`;

const EmployeeManagementContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }

  .header-actions {
    display: flex;
    gap: 1rem;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }
`;

const FiltersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  input {
    width: 100%;
    padding-left: 2.5rem;
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.125rem;
  }
`;

const EmployeesTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  overflow-x: auto;

  @media (max-width: 1200px) {
    overflow-x: auto;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  color: #2d5016;
  font-size: 0.875rem;
  min-width: 1000px;

  @media (max-width: 1200px) {
    min-width: 1000px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const EmployeeRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
  transition: background 0.3s ease;
  min-width: 1000px;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1200px) {
    min-width: 1000px;
  }

  @media (max-width: 1024px) {
    display: block;
    padding: 1.5rem;
    min-width: auto;
  }
`;

const EmployeeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .employee-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .employee-details {
    h3 {
      font-size: 1rem;
      font-weight: 600;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .role {
      color: #666;
      font-size: 0.875rem;
    }
  }

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background: #d4edda;
    color: #155724;
  }

  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }
`;

const LoginStatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.logged-in {
    background: #d4edda;
    color: #155724;
  }

  &.not-logged-in {
    background: #fff3cd;
    color: #856404;
  }
`;

const ContactInfo = styled.div`
  color: #666;
  font-size: 0.875rem;

  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }
`;

const ActionsColumn = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }

  &.delete:hover {
    border-color: #dc3545;
    color: #dc3545;
  }

  &.email:hover {
    border-color: #2d5016;
    color: #2d5016;
  }
`;

const MobileEmployeeCard = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 1rem;
    }

    .card-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .card-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      color: #2d5016;
    }
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select,
  textarea {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }

    &.error {
      border-color: #dc3545;
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.primary ? "#2d5016" : "white")};
  color: ${(props) => (props.primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmployeeManagement = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "employee",
    department: "",
    position: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch employees from API
  const {
    data: employeesData,
    isLoading: employeesLoading,
    error: employeesError,
    refetch,
  } = useQuery(
    "admin-employees",
    async () => {
      console.log("Making API call to get employees...");
      const result = await usersAPI.getEmployees({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        role: roleFilter || undefined,
      });
      console.log("API call result:", result);
      return result;
    },
    {
      keepPreviousData: true,
    }
  );

  const employees = employeesData?.data?.data?.employees || [];

  // Use employees directly from API (filtering is done server-side)
  const filteredEmployees = employees;

  const handleEmployeeAction = async (employeeId, action) => {
    try {
      if (action === "view") {
        const employee = employees.find((e) => e.id === employeeId);
        setSelectedEmployee(employee);
        setShowViewModal(true);
      } else if (action === "edit") {
        const employee = employees.find((e) => e.id === employeeId);
        setSelectedEmployee(employee);
        setFormData({
          firstName: employee.firstName || "",
          lastName: employee.lastName || "",
          email: employee.email || "",
          phone: employee.phone || "",
          role: employee.role || "employee",
          department: employee.department || "",
          position: employee.position || "",
        });
        setShowEditModal(true);
      } else if (action === "send-email") {
        try {
          const response = await usersAPI.sendWelcomeEmail(employeeId);

          // Check if employee has already logged in
          console.log("Send welcome email response:", response.data);
          if (
            response.data?.hasLoggedIn === false ||
            !response.data?.hasLoggedIn
          ) {
            // Show success toast with temporary password
            toast.success("Welcome email sent successfully!", {
              duration: 6000,
            });

            // Show temporary password in a custom toast
            if (response.data?.temporaryPassword) {
              toast(
                (t) => (
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                      🔐 Temporary Login Credentials
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      <strong>Email:</strong>{" "}
                      {employees.find((e) => e.id === employeeId)?.email}
                    </div>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>Password:</strong>{" "}
                      {response.data.temporaryPassword}
                    </div>
                    <div style={{ fontSize: "0.9em", color: "#666" }}>
                      ✅ Welcome email sent with these credentials
                    </div>
                    <div
                      style={{
                        fontSize: "0.8em",
                        color: "#999",
                        marginTop: "4px",
                      }}
                    >
                      💡 You can send this email again until the employee logs
                      in
                    </div>
                  </div>
                ),
                {
                  duration: 12000,
                  style: {
                    background: "#2d5016",
                    color: "#fff",
                    maxWidth: "400px",
                  },
                }
              );
            }
          } else {
            // Employee has already logged in
            toast.error(
              "This employee has already logged in. Welcome email cannot be sent again.",
              {
                duration: 8000,
              }
            );
          }
        } catch (error) {
          if (
            error.response?.status === 400 &&
            error.response?.data?.data?.hasLoggedIn
          ) {
            // Employee has already logged in
            toast.error(
              "This employee has already logged in. Welcome email cannot be sent again.",
              {
                duration: 8000,
              }
            );
          } else {
            throw error; // Re-throw other errors
          }
        }
      } else if (action === "delete") {
        await usersAPI.deleteEmployee(employeeId);
        toast.success("Employee deleted successfully!");
        refetch();
      }
    } catch (error) {
      console.error(`Error ${action}ing employee:`, error);
      toast.error(`Error ${action}ing employee: ${error.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    if (!formData.department.trim()) {
      newErrors.department = "Department is required";
    }
    if (!formData.position.trim()) {
      newErrors.position = "Position is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (showEditModal && selectedEmployee) {
        await usersAPI.updateEmployee(selectedEmployee.id, formData);
        setShowEditModal(false);
        toast.success("Employee updated successfully!");
      } else {
        const response = await usersAPI.createEmployee(formData);
        setShowAddModal(false);

        // Show success toast with credentials
        if (response.data?.temporaryPassword) {
          toast.success("Employee created successfully!", { duration: 6000 });

          // Show detailed credentials in a custom toast
          toast(
            (t) => (
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                  🔐 Temporary Login Credentials
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Email:</strong> {formData.email}
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Password:</strong> {response.data.temporaryPassword}
                </div>
                <div
                  style={{ fontSize: "0.9em", color: "#666", marginTop: "8px" }}
                >
                  💡 Share these credentials with the employee for their first
                  login.
                </div>
              </div>
            ),
            {
              duration: 10000,
              style: {
                background: "#2d5016",
                color: "#fff",
                maxWidth: "400px",
              },
            }
          );
        } else {
          toast.success("Employee created successfully!");
        }
      }

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "employee",
        department: "",
        position: "",
      });
      setErrors({});
      setSelectedEmployee(null);
      refetch();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(`Error saving employee: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "employee",
      department: "",
      position: "",
    });
    setErrors({});
    setSelectedEmployee(null);
  };

  const departments = [
    "Customer Service",
    "Operations",
    "Marketing",
    "Finance",
    "Human Resources",
    "IT Support",
    "Quality Assurance",
  ];

  const positions = [
    "Support Representative",
    "Delivery Coordinator",
    "Marketing Specialist",
    "Accountant",
    "HR Coordinator",
    "IT Technician",
    "Quality Inspector",
  ];

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const roleOptions = [
    { value: "", label: "All Roles" },
    { value: "employee", label: "Employee" },
    { value: "admin", label: "Admin" },
  ];

  return (
    <EmployeeManagementContainer>
      <EmployeeManagementContent>
        <PageHeader>
          <div>
            <h1>Employee Management</h1>
            <p>Manage employee accounts, roles, and access</p>
          </div>
          <div className="header-actions">
            <ActionButton $primary onClick={() => setShowAddModal(true)}>
              <FiPlus />
              Add New Employee
            </ActionButton>
            <ActionButton>
              <FiDownload />
              Export Data
            </ActionButton>
            <ActionButton>
              <FiRefreshCw />
              Refresh
            </ActionButton>
          </div>
        </PageHeader>

        <FiltersSection>
          <FiltersRow>
            <SearchBar>
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>

            <FilterGroup>
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Role</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>
        </FiltersSection>

        <EmployeesTable>
          <TableHeader>
            <div>Employee</div>
            <div>Status</div>
            <div>Login Status</div>
            <div>Contact</div>
            <div>Department</div>
            <div>Position</div>
            <div>Actions</div>
          </TableHeader>

          {employeesLoading ? (
            <LoadingState>
              <h3>Loading employees...</h3>
            </LoadingState>
          ) : employeesError ? (
            <EmptyState>
              <h3>Error loading employees</h3>
              <p>{employeesError.message}</p>
            </EmptyState>
          ) : filteredEmployees.length === 0 ? (
            <EmptyState>
              <h3>No employees found</h3>
              <p>Try adjusting your search criteria.</p>
            </EmptyState>
          ) : (
            filteredEmployees.map((employee, index) => (
              <React.Fragment key={employee.id}>
                <EmployeeRow
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <EmployeeInfo>
                    <div className="employee-avatar">
                      {employee.firstName.charAt(0)}
                    </div>
                    <div className="employee-details">
                      <h3>
                        {employee.firstName} {employee.lastName}
                      </h3>
                      <div className="role">{employee.role}</div>
                    </div>
                  </EmployeeInfo>

                  <StatusBadge
                    className={employee.isActive ? "active" : "inactive"}
                  >
                    {employee.isActive ? "Active" : "Inactive"}
                  </StatusBadge>

                  <LoginStatusBadge
                    className={
                      employee.hasLoggedIn ? "logged-in" : "not-logged-in"
                    }
                  >
                    {employee.hasLoggedIn ? "Logged In" : "Not Logged In"}
                  </LoginStatusBadge>

                  <ContactInfo>
                    <div className="contact-item">
                      <FiMail />
                      {employee.email}
                    </div>
                    <div className="contact-item">
                      <FiPhone />
                      {employee.phone}
                    </div>
                  </ContactInfo>

                  <div>{employee.department}</div>
                  <div>{employee.position}</div>

                  <ActionsColumn>
                    <ActionIconButton
                      title="View Details"
                      onClick={() => handleEmployeeAction(employee.id, "view")}
                    >
                      <FiEye />
                    </ActionIconButton>
                    <ActionIconButton
                      title="Edit Employee"
                      onClick={() => handleEmployeeAction(employee.id, "edit")}
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      className="email"
                      title="Send Welcome Email"
                      onClick={() =>
                        handleEmployeeAction(employee.id, "send-email")
                      }
                    >
                      <FiSend />
                    </ActionIconButton>
                    <ActionIconButton
                      className="delete"
                      title="Delete Employee"
                      onClick={() =>
                        handleEmployeeAction(employee.id, "delete")
                      }
                    >
                      <FiTrash2 />
                    </ActionIconButton>
                  </ActionsColumn>
                </EmployeeRow>

                <MobileEmployeeCard>
                  <div className="card-header">
                    <EmployeeInfo>
                      <div className="employee-avatar">
                        {employee.firstName.charAt(0)}
                      </div>
                      <div className="employee-details">
                        <h3>
                          {employee.firstName} {employee.lastName}
                        </h3>
                        <div className="role">{employee.role}</div>
                      </div>
                    </EmployeeInfo>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <StatusBadge
                        className={employee.isActive ? "active" : "inactive"}
                      >
                        {employee.isActive ? "Active" : "Inactive"}
                      </StatusBadge>
                      <LoginStatusBadge
                        className={
                          employee.hasLoggedIn ? "logged-in" : "not-logged-in"
                        }
                      >
                        {employee.hasLoggedIn ? "Logged In" : "Not Logged In"}
                      </LoginStatusBadge>
                    </div>
                  </div>

                  <div className="card-content">
                    <ContactInfo>
                      <div className="contact-item">
                        <FiMail />
                        {employee.email}
                      </div>
                      <div className="contact-item">
                        <FiPhone />
                        {employee.phone}
                      </div>
                    </ContactInfo>

                    <div>
                      <div>
                        <strong>Department:</strong> {employee.department}
                      </div>
                      <div>
                        <strong>Position:</strong> {employee.position}
                      </div>
                    </div>
                  </div>

                  <div className="card-actions">
                    <ActionIconButton
                      title="View Details"
                      onClick={() => handleEmployeeAction(employee.id, "view")}
                    >
                      <FiEye />
                    </ActionIconButton>
                    <ActionIconButton
                      title="Edit Employee"
                      onClick={() => handleEmployeeAction(employee.id, "edit")}
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      className="email"
                      title="Send Welcome Email"
                      onClick={() =>
                        handleEmployeeAction(employee.id, "send-email")
                      }
                    >
                      <FiSend />
                    </ActionIconButton>
                    <ActionIconButton
                      className="delete"
                      title="Delete Employee"
                      onClick={() =>
                        handleEmployeeAction(employee.id, "delete")
                      }
                    >
                      <FiTrash2 />
                    </ActionIconButton>
                  </div>
                </MobileEmployeeCard>
              </React.Fragment>
            ))
          )}
        </EmployeesTable>

        {/* Add Employee Modal */}
        {showAddModal && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Add New Employee</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <Form onSubmit={handleSubmit}>
                  <FormRow>
                    <FormGroup>
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={errors.firstName ? "error" : ""}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && (
                        <div className="error-message">{errors.firstName}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={errors.lastName ? "error" : ""}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && (
                        <div className="error-message">{errors.lastName}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? "error" : ""}
                        placeholder="employee@nourishgy.com"
                      />
                      {errors.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={errors.phone ? "error" : ""}
                        placeholder="+592 123-4567"
                      />
                      {errors.phone && (
                        <div className="error-message">{errors.phone}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="role">Role *</label>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className={errors.role ? "error" : ""}
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                      </select>
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="department">Department *</label>
                      <select
                        id="department"
                        name="department"
                        value={formData.department}
                        onChange={handleInputChange}
                        className={errors.department ? "error" : ""}
                      >
                        <option value="">Select department</option>
                        {departments.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                      {errors.department && (
                        <div className="error-message">{errors.department}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="position">Position *</label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className={errors.position ? "error" : ""}
                    >
                      <option value="">Select position</option>
                      {positions.map((pos) => (
                        <option key={pos} value={pos}>
                          {pos}
                        </option>
                      ))}
                    </select>
                    {errors.position && (
                      <div className="error-message">{errors.position}</div>
                    )}
                  </FormGroup>
                </Form>
              </ModalContent>

              <ModalFooter>
                <Button type="button" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  primary
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Employee"}
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}

        {/* View Employee Modal */}
        {showViewModal && selectedEmployee && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Employee Details</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1rem",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #2d5016, #4a7c59)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: "600",
                        fontSize: "1.5rem",
                      }}
                    >
                      {selectedEmployee.firstName.charAt(0)}
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          color: "#2d5016",
                          fontSize: "1.5rem",
                        }}
                      >
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h3>
                      <p style={{ margin: "0.25rem 0", color: "#666" }}>
                        {selectedEmployee.position} •{" "}
                        {selectedEmployee.department}
                      </p>
                      <StatusBadge
                        className={
                          selectedEmployee.isActive ? "active" : "inactive"
                        }
                        style={{ marginTop: "0.5rem" }}
                      >
                        {selectedEmployee.isActive ? "Active" : "Inactive"}
                      </StatusBadge>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "1.5rem",
                    }}
                  >
                    <div>
                      <h4 style={{ color: "#2d5016", marginBottom: "1rem" }}>
                        Contact Information
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div>
                          <strong>Email:</strong> {selectedEmployee.email}
                        </div>
                        <div>
                          <strong>Phone:</strong> {selectedEmployee.phone}
                        </div>
                        <div>
                          <strong>Role:</strong> {selectedEmployee.role}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 style={{ color: "#2d5016", marginBottom: "1rem" }}>
                        Work Information
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div>
                          <strong>Department:</strong>{" "}
                          {selectedEmployee.department}
                        </div>
                        <div>
                          <strong>Position:</strong> {selectedEmployee.position}
                        </div>
                        <div>
                          <strong>Joined:</strong>{" "}
                          {new Date(
                            selectedEmployee.createdAt
                          ).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ModalContent>

              <ModalFooter>
                <Button onClick={handleCloseModal}>Close</Button>
                <Button
                  primary
                  onClick={() => {
                    setShowViewModal(false);
                    handleEmployeeAction(selectedEmployee.id, "edit");
                  }}
                >
                  <FiEdit />
                  Edit Employee
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}

        {/* Edit Employee Modal */}
        {showEditModal && selectedEmployee && (
          <ModalOverlay onClick={handleCloseModal}>
            <Modal
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <h2>Edit Employee</h2>
                <button className="close-button" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </ModalHeader>

              <ModalContent>
                <Form onSubmit={handleSubmit}>
                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-firstName">First Name *</label>
                      <input
                        type="text"
                        id="edit-firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={errors.firstName ? "error" : ""}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && (
                        <div className="error-message">{errors.firstName}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-lastName">Last Name *</label>
                      <input
                        type="text"
                        id="edit-lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={errors.lastName ? "error" : ""}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && (
                        <div className="error-message">{errors.lastName}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-email">Email Address *</label>
                      <input
                        type="email"
                        id="edit-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={errors.email ? "error" : ""}
                        placeholder="employee@nourishgy.com"
                      />
                      {errors.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="edit-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={errors.phone ? "error" : ""}
                        placeholder="+592 123-4567"
                      />
                      {errors.phone && (
                        <div className="error-message">{errors.phone}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="edit-role">Role *</label>
                      <select
                        id="edit-role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className={errors.role ? "error" : ""}
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                      </select>
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="edit-department">Department *</label>
                      <select
                        id="edit-department"
                        name="department"
                        value={formData.department}
                        onChange={handleInputChange}
                        className={errors.department ? "error" : ""}
                      >
                        <option value="">Select department</option>
                        {departments.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                      {errors.department && (
                        <div className="error-message">{errors.department}</div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="edit-position">Position *</label>
                    <select
                      id="edit-position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className={errors.position ? "error" : ""}
                    >
                      <option value="">Select position</option>
                      {positions.map((pos) => (
                        <option key={pos} value={pos}>
                          {pos}
                        </option>
                      ))}
                    </select>
                    {errors.position && (
                      <div className="error-message">{errors.position}</div>
                    )}
                  </FormGroup>
                </Form>
              </ModalContent>

              <ModalFooter>
                <Button type="button" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  primary
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </ModalFooter>
            </Modal>
          </ModalOverlay>
        )}
      </EmployeeManagementContent>
    </EmployeeManagementContainer>
  );
};

export default EmployeeManagement;
